import React from 'react';

import { Link } from 'react-router-dom';

import { Icons, type DomainKey } from '@shared/components/ui';


import { domainHoverProps } from './domainHover';
import SideNavRowContent from './SideNavRowContent';
import { sideNavRowShellClasses } from './sideNavRowShellClasses';

interface SideNavRowLinkProps {
  to: string;
  isCollapsed: boolean;
  icon: keyof typeof Icons;
  /** When set, render the duotone domain-colored icon. */
  domain?: DomainKey;
  label: string;
  active?: boolean;
  /** Accessible name for the icon-only collapsed row (the visible label is hidden). */
  ariaLabel?: string;
  trailing?: React.ReactNode;
  collapsedBadge?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  /** Leave room at the right edge for a control overlaid on the row (a parent's chevron). */
  rightInset?: boolean;
}

/**
 * A single-interactive-element nav row rendered as a Link. Shell classes sit on the anchor itself.
 *
 * A plain Link, not NavLink: NavLink derives its own `aria-current` by prefix
 * match, which marks Inbox (`/attention`) current on Starred and Reading Room.
 * `active` is the caller's longest-match answer and is the only source.
 */
const SideNavRowLink: React.FC<SideNavRowLinkProps> = ({
  to,
  isCollapsed,
  icon,
  domain,
  label,
  active = false,
  ariaLabel,
  trailing,
  collapsedBadge,
  onClick,
  onContextMenu,
  rightInset = false,
}) => {
  const dh = domainHoverProps(domain);
  const padding = rightInset ? 'pl-3 pr-7' : 'px-3';
  const shellClasses = `${sideNavRowShellClasses({ active })} ${dh.className} hover:text-sidebar-foreground w-full ${padding} py-1 text-xs font-medium transition-colors`;
  const content = (
    <SideNavRowContent
      isCollapsed={isCollapsed}
      icon={icon}
      {...(domain ? { domain } : {})}
      label={label}
      trailing={trailing}
      collapsedBadge={collapsedBadge}
    />
  );

  return (
    <Link
      to={to}
      aria-label={ariaLabel}
      {...(active && { 'aria-current': 'page' as const })}
      onClick={onClick}
      onContextMenu={onContextMenu}
      {...(dh.style ? { style: dh.style } : {})}
      className={shellClasses}
    >
      {content}
    </Link>
  );
};

export default SideNavRowLink;
